<template>
  <SBtn
    :id="`menu-btn-${activator}`"
    text
    class="py-1 menu-btn text-initial text-primary nav-menu-btn font16 font-weight-semibold"
    aria-haspopup="menu"
    :aria-expanded="`${menuIsOpened}`"
    @mouseenter="hasHover && handleEnter({ inMenu: true })"
    @mouseleave="hasHover && handleClose({ inMenu: true})"
    @keydown.enter.prevent="stickyOpener = 'keyboard'"
    @keydown.space.prevent="stickyOpener = 'keyboard'"
    @keydown.esc="handleHardClose"
    @keydown.tab="handleTab"
    @focusout="handleFocusOut"
  >
    {{ buttonText }}

    <LayoutHeaderMenuContainer
      :activator="`#menu-btn-${activator}`"
      :is-main-menu="isMainMenu"
      :open="menuIsOpened"
      :is-demo="isDemo"
      :categories="categories"
      :base-category="baseCategory"
      data-testid="side-menu"
      class="mt-3"
      @leave="handleClose({ inButtonMenu: true})"
      @enter="handleEnter({ inButtonMenu: true })"
      @close="handleHardClose"
      @open="stickyOpener = hasHover ? null : 'tap'"
    />
  </SBtn>
</template>

<script setup lang="ts">
import type { CategoryItem } from '~/types/root'
import { useHeaderLayers } from '~/composables/useHeaderLayers'

interface Props {
  isMainMenu?: boolean
  buttonText: string
  isDemo?: boolean
  categories: CategoryItem[]
  baseCategory: CategoryItem
}
const props = withDefaults(defineProps<Props>(), {
  isDemo: false,
  isMainMenu: false
})


const inMenu = ref(false)
const inButtonMenu = ref(false)
// Both keyboard openers need `.prevent`. A native <button> translates Enter (and
// Space) into a synthetic `click`; that click lands on the VMenu activator, which
// toggles the overlay shut ~11ms after we just opened it. Verified live: without
// `.prevent` on Enter, aria-expanded goes true -> false in the same frame pair.
// Do not "simplify" either modifier away.
//
// What opened the menu without hover: the keyboard, or a tap (below). Kept
// separate from `inMenu` (a hover flag cleared on @mouseleave with a grace-period
// timeout) so a keyboard-opened menu isn't slammed shut by a mouse pointer
// crossing and leaving the button, and so tabbing away reliably closes it. One
// ref, so a new way to open the menu cannot be missed in menuIsOpened or in
// handleHardClose. Only keyboard, tap and focus events touch it.
const stickyOpener = ref<'keyboard' | 'tap' | null>(null)

// Touch devices (an iPad, no hover): a tap fires a synthetic mouseenter and then a
// click. Opening on that mouseenter let the click toggle the menu straight back
// shut. Without hover the mouse handlers are off and the tap's click opens the
// menu; a second tap or a tap outside closes it. nuxt2 did the same with
// open-on-hover only when (hover: hover) matched. AB#63678
const { primaryHover: hasHover } = useHoverCapability()

const menuIsOpened = computed(() => inMenu.value || inButtonMenu.value || stickyOpener.value !== null)

// One grace-period timer for both hover flags. A leave adds its flag to the
// pending set and restarts the timer, so two quick leaves (button, then dropdown)
// still clear both. Re-entering within the grace period drops that flag again,
// so a stale timer cannot close the menu under the pointer.
// The two hover regions: the button (inMenu) and its dropdown (inButtonMenu). A
// third region would be keyed by name instead of spelled out per flag below.
type HoverRegions = { inMenu?: boolean, inButtonMenu?: boolean }

let closeTimeout: ReturnType<typeof setTimeout> | null = null
const pendingClose = { inMenu: false, inButtonMenu: false }

const cancelClose = () => {
  if (closeTimeout) { clearTimeout(closeTimeout) }
  closeTimeout = null
  pendingClose.inMenu = false
  pendingClose.inButtonMenu = false
}

const handleClose = (menuValues: HoverRegions) => {
  if (closeTimeout) { clearTimeout(closeTimeout) }
  if (menuValues.inMenu) { pendingClose.inMenu = true }
  if (menuValues.inButtonMenu) { pendingClose.inButtonMenu = true }
  closeTimeout = setTimeout(() => {
    if (pendingClose.inMenu) {
      inMenu.value = false
    }

    if (pendingClose.inButtonMenu) {
      inButtonMenu.value = false
    }
    cancelClose()
  }, 200)
}

// Entering a region cancels only that region's pending close. Moving from the button
// into the dropdown therefore still clears the button's flag when the timer fires,
// while the dropdown's own flag keeps the menu open.
const handleEnter = (menuValues: HoverRegions) => {
  if (menuValues.inMenu) {
    pendingClose.inMenu = false
    inMenu.value = true
  }
  if (menuValues.inButtonMenu) {
    pendingClose.inButtonMenu = false
    inButtonMenu.value = true
  }
  if (!pendingClose.inMenu && !pendingClose.inButtonMenu) { cancelClose() }
}

// Immediate, unconditional close (no hover grace period). Used for scroll-close
// emitted by the menu container — both hover flags must clear or menuIsOpened
// stays true and the dropdown reopens.
const handleHardClose = () => {
  cancelClose()
  inMenu.value = false
  inButtonMenu.value = false
  stickyOpener.value = null
}

// A tap on a childless top-nav category opens this (empty) menu and navigates.
// Close it on the route change, or the button stays aria-expanded on the new page.
const router = useRouter()
watch(() => router.currentRoute.value.fullPath, handleHardClose)

// VMenu teleports its overlay content to <body>, so it is never a DOM
// descendant of the button — plain `event.currentTarget.contains(relatedTarget)`
// cannot see it. Vuetify links activator to overlay via `aria-controls`
// (set on the activator, pointing at the overlay's id), so resolve that id and
// check containment against the overlay element itself. Closes when focus
// leaves both the button and the overlay subtree, or leaves the document
// entirely (relatedTarget === null).
const menuElementOf = (button: HTMLElement | null) => {
  const menuId = button?.getAttribute('aria-controls')
  return menuId ? document.getElementById(menuId) : null
}

const handleFocusOut = (event: FocusEvent) => {
  const button = event.currentTarget as HTMLElement
  const related = event.relatedTarget as Node | null

  if (related && button.contains(related)) { return }

  if (related && menuElementOf(button)?.contains(related)) { return }

  handleHardClose()
}

// Tab from the button of an open menu goes into the dropdown. VMenu teleports the
// dropdown to the end of <body>, so the browser's next Tab stop would be the next
// top-nav button and the menu could never be reached by Tab. Focus moves into it
// here instead; handleFocusOut sees the dropdown via aria-controls and keeps the
// menu open. Shift+Tab closes the menu and lets the browser move focus back. A
// menu with nothing focusable falls through to the native Tab. AB#65421
//
// The stops are the category rows and their links and buttons, not the VList
// root: the root is a Tab stop of its own that only hands focus on to its first
// item. Rows web content hides with d-none (categoryItem.style), and collapsed
// subcategory lists, are skipped: focus() on them does nothing, and Tab from the
// button would then go nowhere.
const MENU_STOP = '.list-item-category, a[href], button'
const TABBABLE = 'button:not([disabled]), [href], input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

// checkVisibility also catches elements hidden by a stylesheet, such as the
// mobile-only header controls at desktop width, which focus() would skip.
const isShown = (el: HTMLElement) =>
  !el.closest('.d-none, [hidden], [style*="display: none"]')
  && (el.checkVisibility?.({ visibilityProperty: true }) ?? true)

// An expander row is not a Tab stop of its own (its inner button is), so only
// elements in the Tab order count.
const menuStops = (button: HTMLElement | null) =>
  Array.from(menuElementOf(button)?.querySelectorAll<HTMLElement>(MENU_STOP) ?? [])
    .filter(el => el.tabIndex >= 0 && isShown(el))

const firstMenuStop = (button: HTMLElement | null) => menuStops(button)[0] ?? null
const lastMenuStop = (button: HTMLElement | null) => menuStops(button).at(-1) ?? null

// The Tab stops after the button, outside its dropdown, in order: usually the
// next top-nav button first.
const stopsAfter = (button: HTMLElement) => {
  const menu = menuElementOf(button)
  return Array.from(document.querySelectorAll<HTMLElement>(TABBABLE)).filter(el =>
    el.tabIndex >= 0
    && isShown(el)
    && !button.contains(el)
    && !menu?.contains(el)
    && Boolean(button.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING)
  )
}

// Focuses the first candidate that actually takes focus. A control the
// selectors cannot rule out may still refuse it, and Tab would then be
// swallowed with focus left on an unmounting menu entry.
const focusFirstOf = (candidates: HTMLElement[]) =>
  candidates.some((el) => {
    el.focus()
    return document.activeElement === el
  })

const handleTab = (event: KeyboardEvent) => {
  if (!menuIsOpened.value) { return }
  if (event.shiftKey) {
    handleHardClose()
    return
  }

  const firstStop = firstMenuStop(event.currentTarget as HTMLElement)
  if (!firstStop) { return }

  event.preventDefault()
  firstStop.focus()
}

// The two edges of the dropdown. The browser would move focus from them to
// whatever is next to the teleported dropdown at the end of <body>, not to the
// top nav. Listened for in the capture phase, ahead of VMenu's own keydown on the
// dropdown, which would only close the menu.
// - Shift+Tab from the first stop goes back to the button, the reverse of
//   handleTab. The menu stays open, so Tab goes back in.
// - Tab from the last stop closes the menu and moves on to the next Tab stop
//   after the button, as if the dropdown sat right after it.
function handleMenuTab (event: KeyboardEvent) {
  if (event.key !== 'Tab') { return }
  const button = document.getElementById(`menu-btn-${activator.value}`)
  if (!button) { return }

  if (event.shiftKey) {
    if (event.target !== firstMenuStop(button)) { return }
    event.preventDefault()
    event.stopPropagation()
    button.focus()
    return
  }

  if (event.target !== lastMenuStop(button)) { return }
  if (!focusFirstOf(stopsAfter(button))) { return }
  event.preventDefault()
  event.stopPropagation()
  handleHardClose()
}

// Register the dropdown as the 'navMenu' header layer, as nuxt2's MenuContainer
// did: opening another layer (user menu, loyalty, cart flyout) closes it, and
// Escape anywhere on the page closes it while it is open. Each button gets its
// own key so a delayed hover-close of one button cannot clear the layer of the
// button the pointer just moved to.
const layers = useHeaderLayers()
const navMenuLayer = computed(() => `navMenu:${activator.value}`)

function handleDocumentKeydown (event: KeyboardEvent) {
  if (event.key === 'Escape') {
    handleHardClose()
  }
}

watch(menuIsOpened, (open) => {
  if (open) {
    layers.open(navMenuLayer.value)
    document.addEventListener('keydown', handleDocumentKeydown)
    document.addEventListener('keydown', handleMenuTab, true)
  } else {
    layers.close(navMenuLayer.value)
    document.removeEventListener('keydown', handleDocumentKeydown)
    document.removeEventListener('keydown', handleMenuTab, true)
  }
})
watch(layers.activeLayer, (layer) => {
  if (layer !== navMenuLayer.value && menuIsOpened.value) {
    handleHardClose()
  }
})

onUnmounted(() => {
  cancelClose()
  layers.close(navMenuLayer.value)
  document.removeEventListener('keydown', handleDocumentKeydown)
  document.removeEventListener('keydown', handleMenuTab, true)
})

const activator = computed(() => props.buttonText
  .toLowerCase()
  .replace(/[^a-z0-9\s-]/g, '')    // remove special chars
  .replace(/\s+/g, '-')            // replace spaces with -
  .replace(/-+/g, '-')             // collapse multiple -
  .replace(/^-+|-+$/g, '')
  .toLowerCase() 
)
</script>
